import { create } from 'zustand';
import apiService from '../services/apiService';

const useTargetStore = create((set, get) => ({
  targets: [],
  targetHierarchy: null,
  isLoading: false,
  error: null,

  // Fetch target hierarchy for a specific year
  fetchTargetHierarchy: async (year) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.get(`/targets/hierarchy/${year}`);
      if (response.data.success) {
        set({ 
          targetHierarchy: response.data.data,
          isLoading: false,
          error: null 
        });
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to fetch target hierarchy' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch target hierarchy';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Fetch all targets
  fetchTargets: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.type) params.append('type', filters.type);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);

      const response = await apiService.get(`/targets?${params}`);
      if (response.data.success) {
        set({ 
          targets: response.data.data,
          isLoading: false,
          error: null 
        });
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to fetch targets' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch targets';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Create a new target
  createTarget: async (targetData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post('/targets', targetData);
      if (response.data.success) {
        // Refresh targets after creation
        await get().fetchTargets({ year: targetData.year });
        set({ isLoading: false, error: null });
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to create target' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create target';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Update a target
  updateTarget: async (targetId, targetData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.put(`/targets/${targetId}`, targetData);
      if (response.data.success) {
        // Update the target in the local state
        set((state) => ({
          targets: state.targets.map(target => 
            target._id === targetId ? response.data.data : target
          ),
          isLoading: false,
          error: null
        }));
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to update target' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update target';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Delete a target
  deleteTarget: async (targetId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.delete(`/targets/${targetId}`);
      if (response.data.success) {
        // Remove the target from local state
        set((state) => ({
          targets: state.targets.filter(target => target._id !== targetId),
          isLoading: false,
          error: null
        }));
        return { success: true };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to delete target' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete target';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Refresh target achievements
  refreshAchievements: async (year) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post('/targets/refresh-achievements', { year });
      if (response.data.success) {
        // Refresh the target hierarchy after updating achievements
        if (year) {
          await get().fetchTargetHierarchy(year);
        }
        set({ isLoading: false, error: null });
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to refresh achievements' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to refresh achievements';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Get target statistics
  fetchTargetStats: async (year) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.get(`/targets/stats/${year}`);
      if (response.data.success) {
        set({ isLoading: false, error: null });
        return { success: true, data: response.data.data };
      } else {
        set({ 
          isLoading: false, 
          error: response.data.message || 'Failed to fetch target stats' 
        });
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch target stats';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Reset store
  reset: () => set({
    targets: [],
    targetHierarchy: null,
    isLoading: false,
    error: null
  })
}));

export default useTargetStore;
